import {createFundamentalType, registerFundamentalNode} from "#template/fundamental/factory"
import {MaterialInfo} from "#template/interface-descriptors"
import type {MaterialGraph} from "@cm/material-nodes"
import {transformOffsetUvs} from "@cm/material-nodes"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {resolveTemplateInput} from "#template/fundamental/helpers"
import {TemplateNode} from "#template/template-node/template-node"
import {materialMarker} from "#template/nodes/material/marker"
import type {MaterialLikeNode} from "#template/nodes/material/marker"

export {isMaterialLikeNode, isMaterialLikeNodeClass, materialLike, materialLikeMarker} from "#template/nodes/material/marker"
export type {MaterialLikeNode} from "#template/nodes/material/marker"

export const MaterialFT = createFundamentalType({
    name: "Material",
    marker: materialMarker,
    InfoClass: MaterialInfo,
    icon: "far fa-game-board-alt",
})

@registerFundamentalNode(MaterialFT, "Likes")
export class MaterialLikes extends MaterialFT.Likes {}

@registerFundamentalNode(MaterialFT, "Switch")
export class MaterialSwitch extends MaterialFT.Switch {}

// MaterialInput is the only fundamental Input that mixes ambient sibling inputs into its result —
// reads `${id}-horizontalOffset`, `-verticalOffset`, `-rotation` and pipes the material graph through
// transformOffsetUvs.
@registerFundamentalNode(MaterialFT, "Input")
export class MaterialInput extends MaterialFT.Input {
    override evaluateImpl(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<{value: MaterialGraph | null; origin: TemplateNode | undefined}> {
        const templateInput = this.getTemplateInput(scope, evaluator)
        if (templateInput === undefined)
            return scope.struct<{value: MaterialGraph | null; origin: TemplateNode | undefined}>("MaterialInputData", {
                value: evaluator.evaluate((this.parameters.default ?? null) as MaterialLikeNode | null),
                origin: this.parameters.default instanceof TemplateNode ? this.parameters.default : undefined,
            })
        else {
            const horizontal = resolveTemplateInput<number>(scope, evaluator, `${this.parameters.id}-horizontalOffset`, "Number")
            const vertical = resolveTemplateInput<number>(scope, evaluator, `${this.parameters.id}-verticalOffset`, "Number")
            const rotation = resolveTemplateInput<number>(scope, evaluator, `${this.parameters.id}-rotation`, "Number")
            if (horizontal !== undefined || vertical !== undefined || rotation !== undefined) {
                return scope.pureLambda(
                    scope.tuple(templateInput, horizontal, vertical, rotation),
                    ([templateInput, horizontal, vertical, rotation]) => {
                        const offset = {
                            horizontal: horizontal?.value ?? 0,
                            vertical: vertical?.value ?? 0,
                            rotation: rotation?.value ?? 0,
                        }
                        return {
                            value: templateInput.value ? transformOffsetUvs(templateInput.value, offset) : null,
                            origin: templateInput.origin,
                        }
                    },
                    "transformOffsetUVs",
                )
            } else return templateInput
        }
    }
}

@registerFundamentalNode(MaterialFT, "Output")
export class MaterialOutput extends MaterialFT.Output {}

@registerFundamentalNode(MaterialFT, "Export")
export class MaterialExport extends MaterialFT.Export {}
